import { useEffect, useRef } from 'react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

type VideoVariant = 'hero' | 'process' | 'footer'

interface VideoBackgroundProps {
  /** Cuál video usar (3 variantes generadas por Higgsfield, una por sección). */
  src: VideoVariant
  /** Blur en px aplicado al video (0 = nítido). Usa 4-8px en secciones con contenido encima. */
  blur?: number
  /** Brightness 0..1 (1 = original, 0.5 = mitad de luminosidad). Útil para oscurecer detrás de texto. */
  brightness?: number
  /** Saturación 0..2 (1 = original, 1.3 = más vívido). */
  saturate?: number
  /** Escala CSS del video (1 = sin escalar). Útil para hacer zoom in/out y que no se vea idéntico entre secciones. */
  scale?: number
  /** Rotación CSS en deg (0 = sin rotar). Útil para que el mismo video se sienta distinto. */
  rotate?: number
  /** Mirror horizontal (scaleX(-1)). Útil para variar el mismo video en distintas secciones. */
  mirror?: boolean
  /** Capa overlay CSS background (gradient o color) encima del video, debajo del contenido. */
  overlay?: string
  /** Clase tailwind extra al contenedor. */
  className?: string
}

const SOURCES: Record<VideoVariant, { hevc: string; webm: string; mp4: string; poster: string }> = {
  hero: {
    hevc: '/brand/bg-hero.hevc.mp4',
    webm: '/brand/bg-hero.webm',
    mp4: '/brand/bg-hero.mp4',
    poster: '/brand/bg-hero-poster.jpg',
  },
  process: {
    hevc: '/brand/bg-process.hevc.mp4',
    webm: '/brand/bg-process.webm',
    mp4: '/brand/bg-process.mp4',
    poster: '/brand/bg-process-poster.jpg',
  },
  footer: {
    hevc: '/brand/bg-footer.hevc.mp4',
    webm: '/brand/bg-footer.webm',
    mp4: '/brand/bg-footer.mp4',
    poster: '/brand/bg-footer-poster.jpg',
  },
}

/**
 * VideoBackground — wrapper reusable del video tricolor de salud.
 *
 * Mismo video MP4 (HEVC + WebM + H.264 fallback) reutilizado en múltiples
 * secciones. El navegador descarga UN solo archivo (caché compartido HTTP),
 * por lo que NO hay penalización de bandwidth al usarlo 3 veces.
 *
 * Performance:
 *  - IntersectionObserver pausa el video cuando NO está en viewport. Sin
 *    esto, 3 videos decodificando simultáneamente saturan GPU integradas.
 *  - `preload="metadata"` evita descargar el archivo completo hasta que
 *    el video entra al viewport.
 *  - `useReducedMotion` mantiene el poster fijo si el OS lo pide.
 *
 * Variar el look entre instancias:
 *  - `blur` (4-12px): desenfoca el video — útil cuando hay texto encima.
 *  - `brightness` (0.4-0.7): oscurece — útil para contraste con texto claro.
 *  - `scale` (1.0-1.3): zoom in — varía la composición visible.
 *  - `mirror` (true): voltea horizontalmente — el mismo loop se ve nuevo.
 *  - `overlay` (CSS bg): capa de color encima — pinta el ambiente final.
 */
export default function VideoBackground({
  src,
  blur = 0,
  brightness = 1,
  saturate = 1,
  scale = 1,
  rotate = 0,
  mirror = false,
  overlay,
  className,
}: VideoBackgroundProps) {
  const sources = SOURCES[src]
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  // IntersectionObserver — pausa el video fuera de viewport para liberar GPU.
  useEffect(() => {
    const v = videoRef.current
    const c = containerRef.current
    if (!v || !c) return
    if (reduced) {
      v.pause()
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            v.play().catch(() => {
              /* autoplay puede fallar si user no interactuó aún */
            })
          } else {
            v.pause()
          }
        }
      },
      { threshold: 0.1 },
    )
    io.observe(c)
    return () => io.disconnect()
  }, [reduced])

  const filterParts: string[] = []
  if (blur > 0) filterParts.push(`blur(${blur}px)`)
  if (brightness !== 1) filterParts.push(`brightness(${brightness})`)
  if (saturate !== 1) filterParts.push(`saturate(${saturate})`)
  const filter = filterParts.length > 0 ? filterParts.join(' ') : undefined

  const transformParts: string[] = []
  if (mirror) transformParts.push('scaleX(-1)')
  if (scale !== 1) transformParts.push(`scale(${scale})`)
  if (rotate !== 0) transformParts.push(`rotate(${rotate}deg)`)
  const transform = transformParts.length > 0 ? transformParts.join(' ') : undefined

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}
    >
      <video
        ref={videoRef}
        key={src}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        poster={sources.poster}
        style={{ filter, transform }}
      >
        <source src={sources.hevc} type='video/mp4; codecs="hvc1"' />
        <source src={sources.webm} type="video/webm" />
        <source src={sources.mp4} type="video/mp4" />
      </video>
      {overlay && <div className="absolute inset-0" style={{ background: overlay }} />}
    </div>
  )
}
